import type React from 'react';
import Button, { type ButtonVariant } from '../atoms/Button';

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: ButtonVariant;
    icon?: React.ReactNode;
  };
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: {
    container: 'py-8',
    icon: 'w-12 h-12 mb-4',
    title: 'text-lg font-medium',
    description: 'text-sm'
  },
  md: {
    container: 'py-12', 
    icon: 'w-16 h-16 mb-6',
    title: 'text-xl font-semibold',
    description: 'text-base'
  },
  lg: {
    container: 'py-16',
    icon: 'w-20 h-20 mb-8', 
    title: 'text-2xl font-bold',
    description: 'text-lg'
  }
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  size = 'md',
  className = ''
}) => {
  const classes = sizeClasses[size];
  
  return (
    <div className={`text-center ${classes.container} ${className}`}>
      {icon && (
        <div className={`mx-auto ${classes.icon} text-content-tertiary flex items-center justify-center`}>
          {icon}
        </div>
      )}
      
      <h3 className={`${classes.title} text-text-primary mb-2`}>
        {title}
      </h3>
      
      {description && (
        <p className={`${classes.description} text-text-secondary mb-6 max-w-md mx-auto leading-relaxed`}>
          {description}
        </p>
      )}
      
      {action && (
        <Button
          onClick={action.onClick}
          variant={action.variant ?? 'primary'}
          size="medium"
          icon={action.icon}
          iconPosition="left"
        >
          {action.label}
        </Button>
      )}
    </div>
  );
};

EmptyState.displayName = 'EmptyState';

export default EmptyState;